import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';

import { SvgIconUrl } from 'common/components';

import svgIcons from 'common/svgIcons';

import { memoizedComponent } from 'utils/memoizeComponent';

import { DropdownOption, DropdownOptionValue } from './Dropdown.component';

interface DropdownSelectProps<T> {
  option: DropdownOption<T>;
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  isActive?: boolean;
  isSubOption?: boolean;
  className?: string;
  searchTerm?: string;
}

function highlightSearchTerm(label: string, searchTerm?: string) {
  if (searchTerm) {
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return label.replace(regex, '<span class="dropdown__item-label--highlighted">$1</span>');
  }
  return label;
}

const DropdownSelectComponent = <T extends DropdownOptionValue>({
  option,
  isActive,
  isSubOption,
  onClick,
  searchTerm,
  className: propsClassName = '',
}: DropdownSelectProps<T>) => {
  const hasOptions = useMemo(() => !!option.options?.length, [option.options]);

  const className = classNames({
    dropdown__item: true,
    'dropdown__item--select': true,
    'dropdown__item--warn': option.warn,
    'dropdown__item--active dropdown__item--select-active': isActive,
    'dropdown__item--parrent': hasOptions,
    'dropdown__item--child': isSubOption,

    [`${propsClassName}__item`]: propsClassName,
    [`${propsClassName}__item--select`]: propsClassName,
    [`${propsClassName}__item--active`]: propsClassName && isActive,
    [`${propsClassName}__item--parrent`]: propsClassName && hasOptions,
    [`${propsClassName}__item--child`]: propsClassName && isSubOption,
  });

  const labelClassName = classNames({
    'dropdown__item-label': true,
    'dropdown__item-label--select': true,
    'dropdown__item-label--active': isActive,

    [`${propsClassName}__item-label`]: propsClassName,
    [`${propsClassName}__item-label--select`]: propsClassName,
    [`${propsClassName}__item-label--active`]: propsClassName && isActive,
  });

  const iconClassName = classNames({
    'dropdown__item-icon': true,
    'dropdown__item-icon--select': true,
    'dropdown__item-icon--active': isActive,

    [`${propsClassName}__item-icon`]: propsClassName,
    [`${propsClassName}__item-icon--select`]: propsClassName,
    [`${propsClassName}__item-icon--active`]: propsClassName && isActive,
  });

  const dropdownItemContentClassName = classNames({
    'dropdown__item-content': true,

    [`${propsClassName}__item-content`]: propsClassName,
  });

  const onRemoveOption = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      event.stopPropagation();
      event.preventDefault();
      option.onRemove?.(option.value);
    },
    [option],
  );

  if (!option || option.hidden) return null;

  if (typeof option.label !== 'string') return <div onClick={onClick}>{option.label}</div>;

  const highlightedLabel = highlightSearchTerm(option.label, searchTerm);

  return (
    <>
      <div
        data-id={option['data-test']}
        data-value={option.value}
        data-test='dropdown-option'
        onClick={onClick}
        className={className}
      >
        <span className={labelClassName}>
          {option.icon && <SvgIconUrl className={iconClassName} symbol={option.icon} />}
          <div className={dropdownItemContentClassName}>
            <p className='dropdown__item-label'>
              <span data-value={option.value} dangerouslySetInnerHTML={{ __html: highlightedLabel }} />
            </p>
            {!!option.description && <span className='dropdown__item-description'>{option.description}</span>}
          </div>
        </span>
        {isActive && !hasOptions && <SvgIconUrl className='dropdown__item-status' symbol={svgIcons.Check()} />}
        {isActive && hasOptions && <SvgIconUrl className='dropdown__item-status' symbol={svgIcons.Plus()} />}
        {option.onRemove && (
          <SvgIconUrl className='dropdown__item-status' symbol={svgIcons.Trash()} onClick={onRemoveOption} />
        )}
      </div>
      {option.options &&
        option.options.map((o, index) => (
          <DropdownSelectComponent key={index} option={o} onClick={onClick} isSubOption />
        ))}
    </>
  );
};

export const DropdownSelect = memoizedComponent(DropdownSelectComponent);
